import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useOpenAlerts } from '@/lib/queries/alerts';
import { refId } from '@/lib/api/refs';
import { useLiveVehicles } from '@/lib/queries/vehicles';
import { deriveLiveVehicle, STATUS_ORDER, type LiveVehicle } from '@/lib/vehicle-status';
import { useLiveStore } from '@/stores/live-store';
import { useUiStore } from '@/stores/ui-store';
import { useNow } from './use-now';
import type { Id, LiveStatus } from '@/types/api';

export interface FleetSnapshot {
  vehicles: LiveVehicle[];
  /** After search/status/group filters — what the drawer and map render. */
  visible: LiveVehicle[];
  counts: Record<LiveStatus, number>;
  /** Vehicles held by a live immobilisation — cuts across the status counts. */
  immobilised: number;
  groups: string[];
  byId: Map<Id, LiveVehicle>;
  isLoading: boolean;
  isError: boolean;
}

/**
 * The one place server state and socket state are merged. Everything downstream
 * — drawer rows, map features, the detail header — reads from this so they can
 * never disagree about a vehicle's status.
 */
export function useFleet(): FleetSnapshot {
  const { data: vehicles, isLoading, isError } = useLiveVehicles();
  const { data: openAlerts } = useOpenAlerts();
  const positions = useLiveStore((s) => s.positions);
  const alerting = useLiveStore((s) => s.alerting);
  // useShallow is mandatory here: zustand v5 compares selector output with
  // Object.is, so a fresh object literal every render would loop forever.
  const { search, statusFilter, groupFilter, sort } = useUiStore(
    useShallow((s) => ({
      search: s.search,
      statusFilter: s.statusFilter,
      groupFilter: s.groupFilter,
      sort: s.sort,
    })),
  );

  // Relative timestamps and the offline cutoff both need a ticking clock.
  const now = useNow(5_000);

  const alertVehicleIds = useMemo(() => {
    const ids = new Set<Id>(Object.keys(alerting));
    for (const alert of openAlerts?.items ?? []) {
      const vehicleId = refId(alert.vehicleId);
      if (vehicleId) ids.add(vehicleId);
    }
    return ids;
  }, [openAlerts, alerting]);

  return useMemo(() => {
    const list = (vehicles ?? []).map((vehicle) =>
      deriveLiveVehicle(vehicle, positions[vehicle.id], alertVehicleIds.has(vehicle.id), now),
    );

    const counts: Record<LiveStatus, number> = { moving: 0, idle: 0, offline: 0, alert: 0 };
    const groups = new Set<string>();
    const byId = new Map<Id, LiveVehicle>();
    let immobilised = 0;
    for (const entry of list) {
      counts[entry.status] += 1;
      // PENDING is a request waiting for a safe stop, not a held vehicle.
      if (entry.vehicle.immobilisation?.state === 'IMMOBILISED') immobilised += 1;
      if (entry.vehicle.group) groups.add(entry.vehicle.group);
      byId.set(entry.vehicle.id, entry);
    }

    const needle = search.trim().toLowerCase();
    const visible = list
      .filter((entry) => {
        if (statusFilter.length > 0 && !statusFilter.includes(entry.status)) return false;
        if (groupFilter && entry.vehicle.group !== groupFilter) return false;
        if (!needle) return true;
        const { plateNumber, make, model, group } = entry.vehicle;
        return [plateNumber, make, model, group]
          .filter(Boolean)
          .some((field) => field!.toLowerCase().includes(needle));
      })
      .sort((a, b) => {
        switch (sort) {
          case 'plate':
            return a.vehicle.plateNumber.localeCompare(b.vehicle.plateNumber);
          case 'speed':
            return b.speedKph - a.speedKph;
          case 'updated':
            return (
              (b.updatedAt ? Date.parse(b.updatedAt) : 0) -
              (a.updatedAt ? Date.parse(a.updatedAt) : 0)
            );
          case 'status':
          default: {
            const delta = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
            return delta !== 0 ? delta : a.vehicle.plateNumber.localeCompare(b.vehicle.plateNumber);
          }
        }
      });

    return {
      vehicles: list,
      visible,
      counts,
      immobilised,
      groups: [...groups].sort(),
      byId,
      isLoading,
      isError,
    };
  }, [vehicles, positions, alertVehicleIds, now, search, statusFilter, groupFilter, sort, isLoading, isError]);
}
